import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiStar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { settingsAPI } from '../../services/api';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const fallbackTestimonials = [
  { _id: '1', name: 'Anjali Sharma',  role: 'Parent of Grade 8 Student', rating: 5, text: "Paradise English School has transformed my daughter's approach to learning. The teachers are incredibly dedicated and the school's focus on holistic development has helped her grow not just academically but as a person." },
  { _id: '2', name: 'Rohan Thapa',    role: 'Alumni, Class of 2022',     rating: 5, text: 'My years at Paradise English School were the foundation of my success. The rigorous academic program, combined with extracurricular activities, prepared me well for university.' },
  { _id: '3', name: 'Priya Shrestha', role: 'Parent of Grade 5 Student', rating: 5, text: 'The quality of education here is outstanding. My son loves going to school every day, which says a lot about the teaching methods and the positive environment the teachers have created.' },
];

const getInitials = (name) =>
  name ? name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) : '??';

const StarRating = ({ rating }) => (
  <div className="flex gap-1 justify-center">
    {[1,2,3,4,5].map((s) => (
      <FiStar
        key={s}
        className={`w-4 h-4 ${s <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
        style={{ fill: s <= rating ? '#f59e0b' : 'none' }}
      />
    ))}
  </div>
);

export default function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [current, setCurrent] = useState(0);
  const { ref, isVisible } = useScrollAnimation();

  useEffect(() => {
    settingsAPI.get().then((res) => {
      const data = res.data?.data?.settings?.testimonials || [];
      if (data.length > 0) setTestimonials(data);
    }).catch(() => {});
  }, []);

  useEffect(() => { setCurrent(0); }, [testimonials.length]);

  useEffect(() => {
    if (testimonials.length <= 1) return;
    const t = setInterval(() => setCurrent((p) => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(t);
  }, [testimonials.length]);

  const prev = () => setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  const next = () => setCurrent((c) => (c + 1) % testimonials.length);
  const item = testimonials[current] || testimonials[0];
  if (!item) return null;

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-primary-50 to-white relative overflow-hidden">
      {/* top gradient accent strip */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500" />
      {/* decorative blurs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary-100 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 opacity-60" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-100 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 opacity-50" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-primary-500 font-bold text-xs uppercase tracking-widest mb-2">Testimonials</p>
          <h2 className="text-4xl font-serif font-bold text-gray-900">What People Say</h2>
        </motion.div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{    opacity: 0, x: -40 }}
              transition={{ duration: 0.35 }}
              className="bg-white border border-gray-100 shadow-xl rounded-3xl p-6 sm:p-8 lg:p-12 text-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4 shadow-lg">
                {getInitials(item.name)}
              </div>
              <StarRating rating={item.rating || 5} />
              <blockquote className="text-gray-600 text-base sm:text-lg leading-relaxed my-4 sm:my-6 italic">
                "{item.text}"
              </blockquote>
              <p className="text-gray-900 font-bold">{item.name}</p>
              <p className="text-primary-500 text-sm">{item.role}</p>
            </motion.div>
          </AnimatePresence>

          {testimonials.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <button onClick={prev} className="w-10 h-10 rounded-full bg-primary-100 hover:bg-primary-500 text-primary-600 hover:text-white flex items-center justify-center transition-colors">
                <FiChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrent(idx)}
                    className={`h-2.5 rounded-full transition-all ${idx === current ? 'bg-primary-500 w-6' : 'w-2.5 bg-primary-200 hover:bg-primary-300'}`}
                  />
                ))}
              </div>
              <button onClick={next} className="w-10 h-10 rounded-full bg-primary-100 hover:bg-primary-500 text-primary-600 hover:text-white flex items-center justify-center transition-colors">
                <FiChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
